import { ref } from 'vue'
import { debounce } from 'lodash-es'

import { moveNodeTo } from '@canvas-builder/util/tree'
import type { ComponentNode } from '@canvas-builder/types/canvas'
import useCanvas from '@canvas-builder/composables/useCanvas'

const currentDraggedComponentId = ref<ComponentNode['id'] | null>(null)
const currentDraggedOverComponentId = ref<ComponentNode['id'] | null>(null)
const isDraggingNode = ref(false)
const highlightedZone = ref<'top' | 'bottom' | 'left' | 'right' | null>(null)
const isTouchEvent = ref(false)
const startedFromHandle = ref(false)

export function useCanvasDnd(id: ComponentNode['id']) {
  function setStartedFromHandle(value: boolean) {
    startedFromHandle.value = value
  }

  function onDragStart(e: DragEvent | TouchEvent) {
    if (!startedFromHandle.value) {
      e.preventDefault()
      return
    } else if (e.type === 'touchstart') {
      isTouchEvent.value = true
    } else if (isTouchEvent.value) {
      return
    }
    console.info(`Starting to drag: ${id}`)
    const dataTransfer = (e as DragEvent).dataTransfer
    if (dataTransfer) {
      dataTransfer.setDragImage(e.target as HTMLElement, 0, 0)
    }
    currentDraggedComponentId.value = id
    isDraggingNode.value = true
  }

  const onDragOver = debounce(
    (e: DragEvent | TouchEvent) => {
      if (!startedFromHandle.value) {
        e.preventDefault()
        return
      } else if (isTouchEvent.value && e.type !== 'touchmove') {
        return
      }

      console.info(`on drag over:`, {
        currentDraggedComponentId: currentDraggedComponentId.value,
        currentDraggedOverComponentId: currentDraggedOverComponentId.value,
        isDraggingNode: isDraggingNode.value,
        highlightedZone: highlightedZone.value,
        isTouchEvent: isTouchEvent.value,
      })
      currentDraggedOverComponentId.value = id
      const rect = (e.target as HTMLElement).getBoundingClientRect()
      const x = (e as DragEvent).clientX - rect.left
      const y = (e as DragEvent).clientY - rect.top
      const width = rect.width
      const height = rect.height

      const topZone = height / 4
      const bottomZone = height - topZone
      const leftZone = width / 4
      const rightZone = width - leftZone

      const { componentNodesById } = useCanvas()
      const draggedOverComponent = componentNodesById.value[currentDraggedOverComponentId.value]

      if (draggedOverComponent && draggedOverComponent.type === 'GridItem') {
        if (x < leftZone) {
          highlightedZone.value = 'left'
        } else if (x > rightZone) {
          highlightedZone.value = 'right'
        } else {
          highlightedZone.value = null
        }
      } else {
        if (y > bottomZone) {
          highlightedZone.value = 'bottom'
        } else if (y < topZone) {
          highlightedZone.value = 'top'
        } else {
          highlightedZone.value = null
        }
      }
    },
    80,
    { maxWait: 240 },
  )

  function onDragEnd(e: DragEvent | TouchEvent) {
    if (!startedFromHandle.value) {
      e.preventDefault()
      return
    } else if (isTouchEvent.value && e.type !== 'touchend') {
      return
    }
    console.info(
      `Ending drag: dropped ${id} on ${currentDraggedOverComponentId.value} with insertionLocation ${highlightedZone.value}`,
    )
    const { setCanvasContent, canvasContent, componentNodesById } = useCanvas()
    if (currentDraggedOverComponentId.value) {
      try {
        const draggedOverComponent = componentNodesById.value[currentDraggedOverComponentId.value]
        const position =
          draggedOverComponent && draggedOverComponent.type === 'GridItem'
            ? highlightedZone.value && ['left', 'right'].includes(highlightedZone.value)
              ? 'after'
              : 'before'
            : highlightedZone.value && ['bottom'].includes(highlightedZone.value)
              ? 'after'
              : 'before'

        setCanvasContent({
          content: moveNodeTo(canvasContent.value, id, currentDraggedOverComponentId.value, position),
        })
      } catch (e) {
        console.warn('Could not update content with DnD: ', e)
      }
    }
    isDraggingNode.value = false
    highlightedZone.value = null
    currentDraggedComponentId.value = null
    currentDraggedOverComponentId.value = null
    isTouchEvent.value = false
    startedFromHandle.value = false
  }

  return {
    isDraggingNode,
    highlightedZone,
    currentDraggedComponentId,
    currentDraggedOverComponentId,
    onDragOver,
    onDragStart,
    onDragEnd,
    setStartedFromHandle,
  }
}
